import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import moment from 'moment';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { t } from 'i18next';
import 'moment-timezone';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import ApproveBack from '../modal/ApproveBack';
import NotisReject from '../modal/NotisReject';

interface Row {
  userId: any;
  dateId: number;
  date: string;
  startTime: string;
  endTime: string;
  categoryid: any;
  description: string;
  allTime: string;
  tasks: any;
  userName: string;
  category: string;
  categoryTree: string;
  can_approve: boolean;
  superVisor: string;
}

interface Props {
  rows: Row[];
  updateRowsAfterStatusChange: () => void;
  loading: boolean;
  setLoading: (v: boolean) => void;
  refreshUserHeder: () => void;
  search: string;
  canReturnWeek?: boolean;
}

const GRADIENTS = [
  'linear-gradient(135deg,#1e40af,#60a5fa)',
  'linear-gradient(135deg,#065f46,#34d399)',
  'linear-gradient(135deg,#7c3aed,#a78bfa)',
  'linear-gradient(135deg,#9a3412,#fb923c)',
  'linear-gradient(135deg,#0e7490,#22d3ee)',
  'linear-gradient(135deg,#1d4ed8,#818cf8)',
  'linear-gradient(135deg,#7e1d5f,#e879f9)',
];

const grad = (i: number) => GRADIENTS[i % GRADIENTS.length];

const getInitials = (name: string) => {
  const p = name.trim().split(' ');
  return (p.length >= 2 ? p[0][0] + p[1][0] : name.slice(0, 2)).toUpperCase();
};

const sumHours = (rws: Row[]) =>
  rws.reduce((a, r) => {
    const [h, m] = r.allTime.split(':').map(Number);
    return a + h + m / 60;
  }, 0);

const fmtHours = (total: number) => {
  const h = Math.floor(total), m = Math.round((total - h) * 60);
  return `${h}:${m.toString().padStart(2, '0')}`;
};

const PendingApprovalsPanel: React.FC<Props> = ({
  rows, updateRowsAfterStatusChange, loading, setLoading, refreshUserHeder, search, canReturnWeek = false,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const [leftWidth, setLeftWidth] = React.useState(() => {
    const saved = localStorage.getItem('approvals_left_width');
    return saved ? Number(saved) : 400;
  });
  const collapsed = leftWidth < 100;
  const containerRef = React.useRef<HTMLDivElement>(null);
  const isResizing = React.useRef(false);

  const handleResizeStart = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizing.current = true;

    const onMouseMove = (ev: MouseEvent) => {
      if (!isResizing.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const newW = Math.max(50, Math.min(600, ev.clientX - rect.left));
      setLeftWidth(newW);
    };
    const onMouseUp = () => {
      isResizing.current = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      // Save width to localStorage
      setLeftWidth(w => { localStorage.setItem('approvals_left_width', String(w)); return w; });
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  const [reportRows, setReportRows]     = useState<Row[]>(rows);
  const [viewMode, setViewMode]         = useState<'employee' | 'category'>('employee');
  const [selectedId, setSelectedId]     = useState<string | null>(null);
  const [processingId, setProcessingId] = useState<number | null>(null);
  const [showBack, setShowBack]         = useState(false);
  const [showReject, setShowReject]     = useState(false);
  const [messege, setMessege]           = useState('');
  const [selUserId, setSelUserId]       = useState<any>(null);
  const [selReportId, setSelReportId]   = useState<any>(null);
  const [selWeek, setSelWeek]           = useState<any>(null);
  const [rejectRow, setRejectRow]       = useState<Row | null>(null);
  // openWeeks: set of week keys that are expanded. Default: all closed.
  const [openWeeks, setOpenWeeks] = useState<Set<string>>(new Set());

  const scrollRef = useRef<number>(0);
  const rightRef  = useRef<HTMLDivElement>(null);

  const toggleWeek = useCallback((week: string) => {
    setOpenWeeks(p => {
      const n = new Set(p);
      n.has(week) ? n.delete(week) : n.add(week);
      return n;
    });
  }, []);

  // Reset open weeks when switching employee/category
  useEffect(() => { setOpenWeeks(new Set()); }, [selectedId]);

  useEffect(() => { setReportRows(rows); }, [rows]);

  // ── Left panel items ──────────────────────────────────────
  const leftItems = useMemo(() => {
    type Item = { id: string; label: string; sub: string; count: number; hours: number };
    const items: Item[] = [];
    const q = search.toLowerCase().trim();

    if (viewMode === 'employee') {
      const g: Record<string, Row[]> = {};
      reportRows.forEach(r => { const k = String(r.userId); (g[k] ??= []).push(r); });
      Object.entries(g)
        .sort(([, a], [, b]) => a[0].userName.localeCompare(b[0].userName))
        .filter(([, rws]) => !q || rws[0].userName.toLowerCase().includes(q))
        .forEach(([id, rws]) =>
          items.push({ id, label: rws[0].userName, sub: '', count: rws.length, hours: sumHours(rws) })
        );
    } else {
      // Group leaf categories by their categoryid
      const leafGroups: Record<string, Row[]> = {};
      reportRows.forEach(r => { const k = String(r.categoryid); (leafGroups[k] ??= []).push(r); });

      // Collect all unique parent prefixes from categoryTree paths
      const parentMap: Record<string, Row[]> = {}; // parentLabel -> all descendant rows
      Object.entries(leafGroups).forEach(([, rws]) => {
        const parts = rws[0].categoryTree.split(' > ');
        // Add all ancestor paths (depth >= 1) as virtual parents
        for (let i = 1; i < parts.length; i++) {
          const parentLabel = parts.slice(0, i).join(' > ');
          (parentMap[parentLabel] ??= []).push(...rws);
        }
      });

      // Leaf items
      const leafItems: typeof items = [];
      Object.entries(leafGroups)
        .sort(([, a], [, b]) => a[0].categoryTree.localeCompare(b[0].categoryTree))
        .filter(([, rws]) => !q || rws[0].categoryTree.toLowerCase().includes(q) || rws.some(r => r.userName.toLowerCase().includes(q)))
        .forEach(([id, rws]) => {
          const uniq = Array.from(new Set(rws.map(r => r.userName)));
          leafItems.push({
            id, label: rws[0].categoryTree,
            sub: `${uniq.length} employee${uniq.length !== 1 ? 's' : ''}`,
            count: rws.length, hours: sumHours(rws),
          });
        });

      // Virtual parent items (id = "parent::label")
      const parentItems: typeof items = [];
      Object.entries(parentMap)
        .filter(([label]) => !q || label.toLowerCase().includes(q) || parentMap[label].some(r => r.userName.toLowerCase().includes(q)))
        .forEach(([label, rws]) => {
          // Only add if this parent label is not already a leaf
          const alreadyLeaf = leafItems.some(i => i.label === label);
          if (!alreadyLeaf) {
            const uniq = Array.from(new Set(rws.map(r => r.userName)));
            parentItems.push({
              id: `parent::${label}`, label,
              sub: `${uniq.length} employee${uniq.length !== 1 ? 's' : ''}`,
              count: rws.length, hours: sumHours(rws),
            });
          }
        });

      // Combine and sort by label
      [...parentItems, ...leafItems]
        .sort((a, b) => a.label.localeCompare(b.label))
        .forEach(item => items.push(item));
    }
    return items;
  }, [reportRows, viewMode, search]);

  useEffect(() => {
    if (leftItems.length && !leftItems.find(i => i.id === selectedId)) {
      setSelectedId(leftItems[0].id);
      setOpenWeeks(new Set());
    } else if (!leftItems.length) setSelectedId(null);
  }, [leftItems, selectedId]);

  // Сбросить аккордеон при смене выбранного сотрудника/категории
  const handleSelectId = useCallback((id: string) => {
    if (id !== selectedId) setOpenWeeks(new Set());
    setSelectedId(id);
  }, [selectedId]);

  // ── Right panel data ──────────────────────────────────────
  const rightRows = useMemo(() => {
    if (!selectedId) return [];
    if (viewMode === 'employee') return reportRows.filter(r => String(r.userId) === selectedId);
    // Category mode: virtual parents start with "parent::", filter by tree prefix
    if (selectedId.startsWith('parent::')) {
      const parentLabel = selectedId.slice('parent::'.length);
      return reportRows.filter(r => r.categoryTree === parentLabel || r.categoryTree.startsWith(parentLabel + ' > '));
    }
    return reportRows.filter(r => String(r.categoryid) === selectedId);
  }, [reportRows, selectedId, viewMode]);

  const weekGroups = useMemo(() => {
    const g: Record<string, Row[]> = {};
    rightRows.forEach(r => {
      const k = `${moment(r.date).year()}-W${moment(r.date).isoWeek()}`;
      (g[k] ??= []).push(r);
    });
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b));
  }, [rightRows]);

  // ── Scroll helpers ────────────────────────────────────────
  const saveScroll = useCallback(() => {
    if (rightRef.current) scrollRef.current = rightRef.current.scrollTop;
  }, []);
  const restScroll = useCallback((ms = 0) => {
    setTimeout(() => { if (rightRef.current) rightRef.current.scrollTop = scrollRef.current; }, ms);
  }, []);

  // ── Actions ───────────────────────────────────────────────
  const doApprove = useCallback(async (dateId: number) => {
    saveScroll(); setProcessingId(dateId); setLoading(true);
    try {
      await axios.put('/time-reports/update-status', { reportsId: [dateId], status: 'fulfilled' });
      enqueueSnackbar('Report approved', { variant: 'success' });
      setReportRows(p => p.filter(r => r.dateId !== dateId));
    } catch { enqueueSnackbar('Error approving', { variant: 'error' }); }
    finally { setProcessingId(null); setLoading(false); restScroll(); }
  }, [enqueueSnackbar, setLoading, saveScroll, restScroll]);

  const doReject = useCallback(async (dateId: number) => {
    if (!messege.trim()) { enqueueSnackbar('Message is empty', { variant: 'error' }); return; }
    saveScroll(); setProcessingId(dateId); setLoading(true);
    try {
      await axios.post('notice/add', {
        message: messege,
        source: `Reject: date: ${moment(rejectRow?.date).format('DD.MM.YYYY')}, ${moment.utc(rejectRow?.startTime).format('HH:mm')}-${moment.utc(rejectRow?.endTime).format('HH:mm')} ${rejectRow?.categoryTree}, ${rejectRow?.allTime}`,
        sourceId: dateId, severity: 'warning', userId: selUserId,
      });
      await axios.put('/time-reports/update-status', { reportsId: [dateId], status: 'rejected', comments: messege });
      setReportRows(p => p.filter(r => r.dateId !== dateId));
      enqueueSnackbar('Report rejected', { variant: 'success' });
    } catch { enqueueSnackbar('Error rejecting', { variant: 'error' }); }
    finally { setProcessingId(null); setLoading(false); setShowReject(false); refreshUserHeder(); restScroll(); }
  }, [messege, rejectRow, selUserId, enqueueSnackbar, setLoading, refreshUserHeder, saveScroll, restScroll]);

  const approveWeek = useCallback(async (identifier: any, week: string, type: 'user' | 'category') => {
    saveScroll(); setLoading(true);
    const toApprove = reportRows.filter(r => {
      const match = type === 'user' ? Number(r.userId) === Number(identifier) : Number(r.categoryid) === Number(identifier);
      return match && `${moment(r.date).year()}-W${moment(r.date).isoWeek()}` === week && r.can_approve;
    });
    try {
      const ids = await Promise.all(toApprove.map(async r => {
        await axios.put('/time-reports/update-status', { reportsId: [r.dateId], status: 'fulfilled' });
        return r.dateId;
      }));
      setReportRows(p => p.filter(r => !ids.includes(r.dateId)));
      enqueueSnackbar(`Approved ${ids.length} reports`, { variant: 'success' });
    } catch { enqueueSnackbar('Error approving week', { variant: 'error' }); }
    finally { setLoading(false); updateRowsAfterStatusChange(); restScroll(300); }
  }, [reportRows, enqueueSnackbar, setLoading, updateRowsAfterStatusChange, saveScroll, restScroll]);

  const BackReportsForWeek = useCallback(async (identifier: number, type: 'user' | 'category', week: string) => {
    saveScroll(); setLoading(true);
    const toReturn = reportRows.filter(r => {
      const match = type === 'user' ? Number(r.userId) === Number(identifier) : Number(r.categoryid) === Number(identifier);
      return match && `${moment(r.date).year()}-W${moment(r.date).isoWeek()}` === week;
    });
    if (!toReturn.every(r => r.can_approve)) {
      enqueueSnackbar('Some reports cannot be returned', { variant: 'error' }); setLoading(false); return;
    }
    try {
      await axios.put('/time-reports/update-status', { reportsId: toReturn.map(r => r.dateId), status: 'new' });
      setReportRows(p => p.filter(r => !toReturn.map(x => x.dateId).includes(r.dateId)));
      enqueueSnackbar('Reports returned', { variant: 'success' });
    } catch { enqueueSnackbar('Error returning', { variant: 'error' }); }
    finally { setLoading(false); setShowBack(false); updateRowsAfterStatusChange(); restScroll(300); }
  }, [reportRows, enqueueSnackbar, setLoading, updateRowsAfterStatusChange, saveScroll, restScroll]);

  const openReject = (dateId: any, userId: any, row: Row) => {
    setRejectRow(row); setSelReportId(dateId); setSelUserId(userId); setMessege(''); setShowReject(true);
  };
  const openBack = (identifier: any, week: string) => {
    setSelReportId(identifier); setSelWeek(week); setShowBack(true);
  };

  // ── Derived ───────────────────────────────────────────────
  const selItem  = leftItems.find(i => i.id === selectedId);
  const selIdx   = leftItems.findIndex(i => i.id === selectedId);
  const maxHours = Math.max(...leftItems.map(i => i.hours), 1);
  const fType    = viewMode === 'employee' ? 'user' : 'category';

  if (loading && !reportRows.length)
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}><CircularProgress /></Box>;

  if (!reportRows.length)
    return <div className="flex items-center justify-center py-[60px] text-[var(--text-color-calendar-week)] text-[14px]">{t('Empty')}</div>;

  // ── Render ────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

    {/* ── Split panel ── */}
    <div ref={containerRef} style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>

      {/* ══ LEFT PANEL ══ */}
      <div className="flex-shrink-0"
        style={{ width: leftWidth, overflowY: 'auto', scrollbarGutter: 'stable', display: 'flex', flexDirection: 'column', transition: isResizing.current ? 'none' : undefined }}>

        {/* View toggle */}
        <div style={{ borderBottom: '1px solid var(--border-input)', flexShrink: 0, padding: collapsed ? '8px 4px' : '10px 12px 8px', background: 'var(--bg-top)' }}>
          {!collapsed && (
            <p className="text-[12px] font-semibold text-[var(--text-color-calendar-week)] mb-[6px]">
              {leftItems.length}&nbsp;{viewMode === 'employee' ? 'Employees' : t('Category')}
            </p>
          )}
          {collapsed ? (
            /* Compact: two icon buttons */
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {(['employee', 'category'] as const).map(mode => (
                <button key={mode} type="button"
                  onClick={() => { setViewMode(mode); setSelectedId(null); }}
                  style={{
                    width: '100%', padding: '5px 0', borderRadius: 5, border: 'none', cursor: 'pointer', fontSize: 10, fontWeight: 700,
                    background: viewMode === mode ? 'var(--btn-user-in)' : 'var(--btn-user-form)',
                    color: viewMode === mode ? 'var(--text-color)' : 'var(--text-color-calendar-week)',
                  }}>
                  {mode === 'employee' ? '👤' : '📂'}
                </button>
              ))}
            </div>
          ) : (
            <div className="flex gap-[3px] rounded-[5px] p-[2px] bg-[var(--btn-user-form)]"
              style={{ border: '1px solid var(--border-input)' }}>
              {(['employee', 'category'] as const).map(mode => (
                <button key={mode} type="button"
                  onClick={() => { setViewMode(mode); setSelectedId(null); }}
                  className="flex-1 rounded-[5px] text-[12px] font-semibold py-[4px] border-none cursor-pointer"
                  style={{
                    background: viewMode === mode ? 'var(--btn-user-in)' : 'transparent',
                    color: viewMode === mode ? 'var(--text-color)' : 'var(--text-color-calendar-week)',
                  }}
                >{mode === 'employee' ? 'Employee' : t('Category')}</button>
              ))}
            </div>
          )}
        </div>

        {/* List */}
        <div style={{ padding: 6 }}>
          {viewMode === 'employee' ? (
            /* ── Employee: flat list with avatar ── */
            leftItems.map((item, idx) => {
              const active = item.id === selectedId;
              return (
                <div key={item.id} onClick={() => handleSelectId(item.id)}
                  className="flex items-center gap-[8px] rounded-[5px] cursor-pointer mb-[2px]"
                  style={{
                    padding: '7px 9px',
                    background: active ? 'var(--btn-user-form)' : 'transparent',
                    border: active ? '1px solid var(--border-input)' : '1px solid transparent',
                  }}
                >
                  <div className="flex items-center justify-center rounded-full text-[10px] font-bold text-white flex-shrink-0"
                    style={{ width: 30, height: 30, background: grad(idx) }}>
                    {getInitials(item.label)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold text-[var(--text-color)] truncate">{item.label}</div>
                  </div>
                  <div className="flex flex-col items-end gap-[2px] flex-shrink-0">
                    <span className="text-[11px] font-medium rounded-full px-[7px] py-[2px]"
                      style={{ background: 'rgba(217,119,6,.12)', color: '#d97706' }}>
                      {item.count}
                    </span>
                    <span className="text-[10px] text-[var(--text-color-calendar-week)]">{fmtHours(item.hours)}</span>
                  </div>
                </div>
              );
            })
          ) : (
            /* ── Category: hierarchical tree ── */
            (() => {
              // Build rendered list with group headers
              const nodes: React.ReactNode[] = [];
              const itemIds = new Set(leftItems.map(i => i.id));
              let lastRoot = '';

              leftItems
                .slice()
                .sort((a, b) => a.label.localeCompare(b.label))
                .forEach((item, idx) => {
                  const parts = item.label.split(' > ');
                  const depth = parts.length - 1;
                  const root = parts[0];
                  const leafLabel = parts[parts.length - 1];
                  const active = item.id === selectedId;

                  // Root group header (when root changes and root itself isn't a selectable item at depth 0)
                  if (depth > 0 && root !== lastRoot) {
                    lastRoot = root;
                    // Check if root itself is already in leftItems (depth 0)
                    const rootInItems = leftItems.find(i => i.label === root);
                    if (!rootInItems) {
                      nodes.push(
                        <div key={`grp-${root}`}
                          className="px-[10px] pt-[8px] pb-[2px] text-[10px] font-800 uppercase tracking-widest text-[var(--text-color-calendar-week)] select-none"
                          style={{ letterSpacing: '.07em', fontWeight: 800 }}>
                          {root}
                        </div>
                      );
                    }
                  } else if (depth === 0) {
                    lastRoot = root;
                  }

                  // The item row
                  nodes.push(
                    <div key={item.id}
                      onClick={() => handleSelectId(item.id)}
                      className="flex items-center gap-[6px] rounded-[5px] cursor-pointer mb-[1px]"
                      style={{
                        paddingTop: 5, paddingBottom: 5,
                        paddingRight: 9,
                        paddingLeft: 9 + depth * 14,
                        background: active ? 'var(--btn-user-form)' : 'transparent',
                        border: active ? '1px solid var(--border-input)' : '1px solid transparent',
                      }}
                    >
                      {/* Tree connector */}
                      {depth > 0 && (
                        <span style={{ color: 'var(--text-color-calendar-week)', fontSize: 10, flexShrink: 0, opacity: .6 }}>└</span>
                      )}

                      {/* Folder icon for depth-0 items */}
                      {depth === 0 && (
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                          style={{ color: 'var(--text-color-calendar-week)', flexShrink: 0 }}>
                          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                        </svg>
                      )}

                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-[var(--text-color)] truncate"
                          style={{ fontSize: depth === 0 ? 12 : 11 }}>
                          {leafLabel}
                        </div>
                        {depth > 0 && (
                          <div className="text-[10px] text-[var(--text-color-calendar-week)]">{item.sub}</div>
                        )}
                      </div>

                      <div className="flex flex-col items-end gap-[1px] flex-shrink-0">
                        <span className="text-[10px] font-medium rounded-full px-[6px] py-[1px]"
                          style={{ background: 'rgba(217,119,6,.12)', color: '#d97706' }}>
                          {item.count}
                        </span>
                        <span className="text-[10px] text-[var(--text-color-calendar-week)]">{fmtHours(item.hours)}</span>
                      </div>
                    </div>
                  );
                });

              return nodes;
            })()
          )}
        </div>
      </div>

      {/* ══ RESIZE HANDLE ══ */}
      <div
        onMouseDown={handleResizeStart}
        style={{
          width: 5, flexShrink: 0, cursor: 'col-resize',
          background: 'var(--border-input)',
          position: 'relative',
          transition: 'background .15s',
        }}
        onMouseEnter={e => (e.currentTarget.style.background = 'rgba(37,99,235,.35)')}
        onMouseLeave={e => (e.currentTarget.style.background = 'var(--border-input)')}
      >
        {/* dots indicator */}
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', display: 'flex', flexDirection: 'column', gap: 3 }}>
          {[0,1,2].map(i => <div key={i} style={{ width: 3, height: 3, borderRadius: '50%', background: 'var(--text-color-calendar-week)', opacity: .5 }} />)}
        </div>
      </div>

      {/* ══ RIGHT PANEL ══ */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Selected item header */}
        {selItem && (
          <div className="flex items-center gap-[10px] px-[15px] py-[7px] bg-[var(--bg-top)]"
            style={{ borderBottom: '1px solid var(--border-input)', flexShrink: 0 }}>
            <div className="flex items-center justify-center rounded-full text-[11px] font-bold text-white flex-shrink-0"
              style={{ width: 28, height: 28, background: grad(selIdx) }}>
              {getInitials(selItem.label)}
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-[14px] font-semibold text-[var(--text-color)]">{selItem.label}</span>
              {selItem.sub && <span className="text-[11px] text-[var(--text-color-calendar-week)] ml-[8px]">{selItem.sub}</span>}
            </div>
            <span className="text-[11px] text-[var(--text-color-calendar-week)] flex-shrink-0">
              {selItem.count} reports · {fmtHours(selItem.hours)}h · {weekGroups.length} {weekGroups.length === 1 ? 'week' : 'weeks'}
            </span>
          </div>
        )}

        {/* Scrollable week blocks */}
        <div ref={rightRef}
          className="flex flex-col gap-[8px] p-[12px]"
          style={{ flex: 1, overflowY: 'auto', scrollbarGutter: 'stable', alignItems: 'stretch', justifyContent: 'flex-start', alignContent: 'flex-start' }}>

          {!weekGroups.length && (
            <div className="flex items-center justify-center py-[40px] text-[var(--text-color-calendar-week)] text-[13px]">
              {t('Empty')}
            </div>
          )}

          {weekGroups.map(([week, weekRows]) => {
            const sorted     = [...weekRows].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
            const weekNum    = moment(weekRows[0].date).isoWeek();
            const weekStart  = moment(weekRows[0].date).startOf('isoWeek').format('DD MMM');
            const weekEnd    = moment(weekRows[0].date).endOf('isoWeek').format('DD MMM YYYY');
            const identifier = viewMode === 'employee' ? Number(selectedId) : weekRows[0].categoryid;

            const isOpen = openWeeks.has(week);

            return (
              <div key={week} className="dashboard-card"
                style={{ borderRadius: 8, flex: '0 0 auto' }}>

                {/* Week header — кликабелен */}
                <div className="flex items-center justify-between flex-wrap gap-[8px] px-[15px] py-[8px] bg-[var(--bg-top)] cursor-pointer select-none rounded-t-[5px]"
                  style={{ borderBottom: isOpen ? '1px solid var(--border-input)' : 'none' }}
                  onClick={() => toggleWeek(week)}>
                  <div className="flex items-center gap-[8px]">
                    <span className="text-[11px] font-semibold text-[var(--text-color-calendar-week)] transition-transform"
                      style={{ display: 'inline-block', transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                      ▶
                    </span>
                    <div>
                      <span className="text-[11px] font-semibold text-[var(--text-color-calendar-week)]">
                        Week {weekNum} ·&nbsp;
                      </span>
                      <span className="text-[13px] font-semibold text-[var(--text-color)]">
                        {weekStart} – {weekEnd}
                      </span>
                      <span className="text-[11px] text-[var(--text-color-calendar-week)] ml-[8px]">
                        {sorted.length} records
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-[7px]" onClick={e => e.stopPropagation()}>
                    <span className="text-[12px] font-semibold text-[var(--text-color)]">
                      {t('Total')}: {fmtHours(sumHours(sorted))}
                    </span>
                    {canReturnWeek && (
                      <button type="button"
                        className="bg-[var(--btn-user-btn)] text-[var(--text-color)] border-none py-[4px] px-[10px] rounded-[5px] cursor-pointer hover:bg-[var(--btn-user-hover)] text-[12px] font-semibold"
                        disabled={loading}
                        onClick={() => openBack(identifier, week)}
                      >↩ {t('RemoveAllForWeek')}</button>
                    )}
                    <button type="button"
                      className="border-none py-[4px] px-[10px] rounded-[5px] cursor-pointer text-[12px] font-semibold"
                      style={{ background: 'rgba(5,150,105,.12)', color: '#059669' }}
                      disabled={loading}
                      onClick={() => approveWeek(identifier, week, fType)}
                    >✓ {t('ApproveAllForWeek')}</button>
                  </div>
                </div>

                {/* Rows */}
                {isOpen && sorted.map((row, idx) => {
                  const busy     = processingId === row.dateId;
                  const disabled = busy || !row.can_approve;
                  return (
                    <div key={row.dateId}
                      className="flex items-center gap-[10px] px-[15px] py-[8px] hover:bg-[var(--hover)]"
                      style={{
                        borderBottom: idx < sorted.length - 1 ? 'var(--border-input)' : 'none',
                        opacity: busy ? .6 : 1,
                        transition: 'background .12s, opacity .15s',
                      }}>

                      {/* Date card */}
                      <div className="flex flex-col items-center rounded-[5px] flex-shrink-0 bg-[var(--bg-top)]"
                        style={{ padding: '3px 7px', minWidth: 36, border: '1px solid var(--border-input)' }}>
                        <span className="text-[15px] font-semibold leading-none text-[var(--text-color)]"
                          style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {moment(row.date).format('D')}
                        </span>
                        <span className="text-[8px] font-semibold uppercase text-[var(--text-color-calendar-week)]"
                          style={{ letterSpacing: '.06em' }}>
                          {moment(row.date).format('ddd')}
                        </span>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-[6px] flex-wrap">
                          <span className="text-[13px] font-semibold text-[var(--text-color)] flex-shrink-0"
                            style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {moment.utc(row.startTime).format('HH:mm')} – {moment.utc(row.endTime).format('HH:mm')}
                          </span>
                          <span className="text-[11px] font-semibold text-[var(--text-color)] rounded-[5px] px-[6px] py-[1px] bg-[var(--btn-user-in)] whitespace-nowrap flex-shrink-0">
                            {row.categoryTree}
                          </span>
                          {row.tasks?.length > 0 && (
                            <span className="text-[11px] text-[var(--text-color-calendar-week)]">
                              {row.tasks.map((tk: any) => tk.name).join(', ')}
                            </span>
                          )}
                        </div>

                        {(viewMode === 'category' || row.superVisor) && (
                          <div className="flex items-center gap-[10px] mt-[2px] flex-wrap">
                            {viewMode === 'category' && (
                              <span className="text-[12px] font-semibold text-[var(--text-color)]">{row.userName}</span>
                            )}
                            {row.superVisor && (
                              <span className="text-[11px] text-[var(--text-color-calendar-week)]">
                                {t('Supervisor')}: <span className="font-semibold text-[var(--text-color)]">{row.superVisor}</span>
                              </span>
                            )}
                          </div>
                        )}

                        {row.description && (
                          <div className="text-[11px] text-[var(--text-color-calendar-week)] mt-[1px]">
                            {row.description}
                          </div>
                        )}
                      </div>

                      {/* Hours */}
                      <div className="flex items-baseline gap-[1px] flex-shrink-0">
                        <span className="text-[18px] font-semibold text-[var(--text-color)]"
                          style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {row.allTime.split(':')[0]}
                        </span>
                        <span className="text-[12px] text-[var(--text-color-calendar-week)]">
                          :{row.allTime.split(':')[1]}h
                        </span>
                      </div>

                      {/* Buttons */}
                      <div className="flex flex-col gap-[4px] flex-shrink-0">
                        <button type="button"
                          disabled={disabled}
                          onClick={() => doApprove(row.dateId)}
                          className="text-[11px] font-semibold py-[3px] px-[9px] rounded-[5px] border-none"
                          style={{
                            background: disabled ? 'var(--btn-user-btn)' : 'rgba(5,150,105,.12)',
                            color: disabled ? 'var(--text-color-calendar-week)' : '#059669',
                            cursor: disabled ? 'not-allowed' : 'pointer',
                          }}
                        >✓ {t('Approve')}</button>
                        <button type="button"
                          disabled={disabled}
                          onClick={() => openReject(row.dateId, row.userId, row)}
                          className="bg-[var(--btn-user-btn)] text-[var(--text-color)] text-[11px] font-semibold py-[3px] px-[9px] rounded-[5px] border-none"
                          style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
                        >↩ {t('Return')}</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

    </div>

      {showReject && (
        <NotisReject showReject={showReject} setReject={setShowReject} rejectReport={doReject} reportId={selReportId} setMessege={setMessege} />
      )}
      {showBack && (
        <ApproveBack showAprove={showBack} setShowAprove={setShowBack} BackReportsForWeek={BackReportsForWeek} reportId={selReportId} filterType={fType} week={selWeek} />
      )}
    </div>
  );
};

export default PendingApprovalsPanel;
